/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import Invoice from '../../../models/invoiceModel';
import History from '../../../models/historyModel';
import {setNavigationParam} from '../../../util/navigateUtil';
import {sendGaPageView} from '../../../util/analytics';
import {downloadFile, navigate, ResponseData} from '@dvrd/dvr-controls';
import InvoiceDetails from './invoiceDetails';
import {onInvoicePreview, onSetCrumbs} from '../../../util/events';
import {InvoiceType} from '../../../util/interfaces';
import {downloadInvoice} from '../../../util/requestSender';
import {AppContext} from '../../misc/appContext';
import {createErrorMessage} from '../../../util/utils';
import {CREDIT_DATA_ROUTE, EDIT_INVOICE_ROUTE} from '../../../util/constants';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

export default function InvoiceDetailsController() {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const [invoice, setInvoice] = useState(new Invoice());
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [loadingInvoice, setLoadingInvoice] = useState(true);
    const [history, setHistory] = useState<Array<History>>([]);

    function onClose() {
        window.history.back();
    }

    function onClickEdit() {
        if (invoice.invoiceType === InvoiceType.DEBET) {
            setNavigationParam(invoice);
            navigate(EDIT_INVOICE_ROUTE);
        } else navigate(`${CREDIT_DATA_ROUTE}?invoice-id=${invoice.referenceInvoiceId}`);
    }

    function onClickPreview() {
        setLoadingHistory(true);
        downloadInvoice({
            invoice_id: invoice.id,
            account_id: invoice.accountId,
            noHistory: true,
            callback: (data: ResponseData, response: Response) => {
                setLoadingHistory(false);
                if (data.status === 'success') {
                    // FILE RESPONSE
                    response.blob().then(blob => {
                        onInvoicePreview(URL.createObjectURL(blob));
                    });
                } else if (!data.handled) {
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('invoice.details.downloadFailed', 'Het downloaden van de factuur is niet gelukt.')
                        ),
                        t('invoice.details.downloadFailedTitle', 'Downloaden mislukt')
                    );
                }
            }
        });
    }

    function onClickDownload() {
        setLoadingHistory(true);
        downloadInvoice({
            invoice_id: invoice.id,
            account_id: invoice.accountId,
            callback: (data: ResponseData, response: Response) => {
                setLoadingHistory(false);
                if (data.status === 'success') {
                    // FILE RESPONSE
                    const header = response.headers.get('Content-Disposition');
                    let fileName = `${invoice.reference}.pdf`;
                    if (header)
                        fileName = header.substring(header.indexOf('filename=') + 'filename='.length, header.length);
                    response.blob().then(blob => {
                        downloadFile(blob, fileName);
                    });
                } else if (!data.handled) {
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('invoice.details.downloadFailed', 'Het downloaden van de factuur is niet gelukt.')
                        ),
                        t('invoice.details.downloadFailedTitle', 'Downloaden mislukt')
                    );
                }
            }
        });
    }

    function onClickSend() {
        showSnackbar(t('invoice.details.sending', 'De factuur wordt verstuurd...'));
        invoice.send((invoice: Invoice, success: boolean, data: ResponseData) => {
            if (success) {
                setInvoice(invoice);
                loadHistory(invoice);
                showSnackbar(t('invoice.details.sendSuccess', 'De factuur is succesvol verstuurd'));
            } else if (!data.handled)
                showDialog(
                    createErrorMessage(
                        data.message ??
                            t('invoice.details.sendFailed', 'Er is iets misgegaan bij het verzenden van de factuur.')
                    ),
                    t('invoice.details.sendFailedTitle', 'Verzenden mislukt')
                );
        });
    }

    function loadHistory(invoice: Invoice) {
        if (!invoice.id) return;
        setLoadingHistory(true);
        History.getAll(invoice, (history: History[]) => {
            setLoadingHistory(false);
            setHistory(history);
        });
    }

    function getInvoice() {
        const invoiceId = new URLSearchParams(location.search).get('id');
        const {account} = context.accountContext;
        if (!invoiceId || !account) return window.history.back();
        setLoadingInvoice(true);
        Invoice.get(invoiceId, account.id, (invoice: Invoice | null) => {
            setLoadingInvoice(false);
            if (!invoice) return window.history.back();
            setInitial(invoice);
        });
    }

    function setInitial(invoice: Invoice) {
        sendGaPageView('/factuur-details');
        onSetCrumbs([
            {label: t('invoice.details.crumbs.invoices', 'Facturen'), route: '/facturen'},
            {
                label: `${t('invoice.details.crumbs.order', 'Order')} ${invoice.orderId}`,
                route: '/factuur-details'
            }
        ]);
        setInvoice(invoice);
        loadHistory(invoice);
    }

    useEffect(() => {
        getInvoice();
    }, []);

    return (
        <InvoiceDetails
            onClickEdit={onClickEdit}
            onClose={onClose}
            onClickDownload={onClickDownload}
            onClickSend={onClickSend}
            onClickPreview={onClickPreview}
            invoice={invoice}
            history={history}
            loadingHistory={loadingHistory}
            loadingInvoice={loadingInvoice}
        />
    );
}
